import { useEffect, useState } from "react";
import {
  adminGetCategories,
  adminCreateCategory,
  adminUpdateCategory,
  adminDeleteCategory,
} from "../../utils/api";
import "./AdminPage.css";

const EMPTY = { name: "", slug: "", image: null };

// Auto-generate slug from Ukrainian name
const toSlug = (str) =>
  str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\u0400-\u04FF-]/g, "")
    .replace(/-+/g, "-")
    .trim("-");

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    adminGetCategories()
      .then((r) => setCategories(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setPreview(null);
    setError("");
    setModal(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, slug: cat.slug, image: null });
    setPreview(
      cat.image
        ? cat.image.startsWith("http")
          ? cat.image
          : `${cat.image}`
        : null
    );
    setError("");
    setModal(true);
  };

  const handleChange = (e) => {
    if (e.target.name === "image") {
      const file = e.target.files[0];
      setForm((f) => ({ ...f, image: file }));
      if (file) setPreview(URL.createObjectURL(file));
    } else if (e.target.name === "name") {
      setForm((f) => ({ ...f, name: e.target.value, slug: toSlug(e.target.value) }));
    } else {
      setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) {
      setError("Назва та slug обов'язкові");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("slug", form.slug);
      if (form.image) fd.append("image", form.image);
      if (editing) await adminUpdateCategory(editing.id, fd);
      else await adminCreateCategory(fd);
      setModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Помилка збереження");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити цю категорію?")) return;
    try {
      await adminDeleteCategory(id);
      load();
    } catch {
      alert("Помилка видалення");
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Категорії</h1>
        <button className="admin-btn primary" onClick={openCreate}>
          + Додати категорію
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Фото</th>
                <th>Назва</th>
                <th>Slug</th>
                <th>Дата</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{ textAlign: "center", padding: 40, color: "var(--text-light)" }}
                  >
                    Категорій немає
                  </td>
                </tr>
              ) : (
                categories.map((cat) => {
                  const imgUrl = cat.image
                    ? cat.image.startsWith("http")
                      ? cat.image
                      : `${cat.image}`
                    : null;
                  return (
                    <tr key={cat.id}>
                      <td>
                        {imgUrl ? (
                          <img src={imgUrl} alt={cat.name} className="admin-thumb" />
                        ) : (
                          <div className="admin-placeholder">🏷️</div>
                        )}
                      </td>
                      <td>
                        <strong>{cat.name}</strong>
                      </td>
                      <td>
                        <code style={{ fontSize: "0.8rem", color: "var(--text-light)" }}>
                          {cat.slug}
                        </code>
                      </td>
                      <td>{new Date(cat.createdAt).toLocaleDateString("uk-UA")}</td>
                      <td>
                        <div className="admin-table-actions">
                          <button className="admin-btn secondary" onClick={() => openEdit(cat)}>
                            Редагувати
                          </button>
                          <button className="admin-btn danger" onClick={() => handleDelete(cat.id)}>
                            Видалити
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(false);
          }}
        >
          <div className="admin-modal">
            <h2>{editing ? "Редагувати категорію" : "Нова категорія"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Назва *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Наприклад: Книжки"
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>
                  Slug *{" "}
                  <span style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>
                    (генерується автоматично)
                  </span>
                </label>
                <input
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder="knyzhky"
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Зображення категорії</label>
                <input name="image" type="file" accept="image/*" onChange={handleChange} />
                {preview && (
                  <img
                    src={preview}
                    alt="preview"
                    style={{ marginTop: 8, height: 80, borderRadius: 8, objectFit: "cover" }}
                  />
                )}
              </div>
              {error && (
                <p style={{ color: "#dc2626", fontSize: "0.85rem", marginBottom: 8 }}>{error}</p>
              )}
              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setModal(false)}
                >
                  Скасувати
                </button>
                <button type="submit" className="admin-btn primary" disabled={submitting}>
                  {submitting ? "Збереження..." : editing ? "Зберегти" : "Створити"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
